
import { computed, defineComponent, reactive, toRefs, watch, ref } from 'vue'
import { XRange } from '@/lib/graph/types/initResult'
import moment from 'moment'

const years = computed(() => {
  const nowYear = moment().year()
  const yearList = [] as number[]
  yearList.push(nowYear)
  for (let i = 0; i < 100; i++) {
    yearList.push(nowYear - (i + 1))
  }
  return yearList
})

export default defineComponent({
  components: {},
  props: {
    reportType: {
      type: String,
      required: true,
      default: 'day',
    },
    center: {
      type: Number,
      default: () => moment().valueOf(),
    },
  },
  emits: ['update:center'],
  setup(props, { emit }) {
    const state = reactive({
      days: [] as Date[],
      weekdays: ['日', '月', '火', '水', '木', '金', '土'],
      selected: -1,
      years,
      selectedValue: {
        date: moment(props.center).date(),
        year: moment(props.center).year(),
        month: moment(props.center).month(),
      },
      isOpen: false,
      reportType: props.reportType,
    })
    const picked = ref<Date>(new Date(props.center))

    const currentDateStr = computed(() => {
      const center = props.center

      switch (state.reportType) {
        case 'hour':
          return moment(center).format('YYYY年MM月DD日')
        case 'day':
          return moment(center).format('YYYY年MM月DD日')
        case 'month':
          return moment(center).format('YYYY年MM月')
        case 'year':
          return moment(center).format('YYYY年')
        default:
          return ''
      }
    })

    const days = computed(() => {
      const current = moment(picked.value.getTime())
      const daysInMonth = current.daysInMonth()
      const firstDay = current.clone().startOf('month')
      const lastDay = firstDay.clone().add(daysInMonth - 1, 'days')
      const firstDayWeekday = firstDay.clone().weekday()
      const lastDayWeekday = lastDay.clone().weekday()
      const calenderObj = [] as Date[]

      for (let i = 0; i < firstDayWeekday; i++) {
        const date = firstDay
          .clone()
          .subtract(i + 1, 'days')
          .toDate()
        calenderObj.unshift(date)
      }

      for (let i = 0; i < daysInMonth; i++) {
        const date = firstDay.clone().add(i, 'days').toDate()
        calenderObj.push(date)
      }

      for (let i = 0; i < 6 - lastDayWeekday; i++) {
        const date = lastDay
          .clone()
          .add(i + 1, 'days')
          .toDate()
        calenderObj.push(date)
      }

      const currentYear = moment(current).year()
      const currentMonth = moment(current).month()
      const currentDate = moment(current).date()
      const selected = calenderObj.findIndex(v => {
        const year = moment(v.getTime()).year()
        const month = moment(v.getTime()).month()
        const date = moment(v.getTime()).date()

        return currentYear == year && currentMonth == month && currentDate == date
      })

      return { calender: calenderObj, selected }
    })

    const setDays = (type?: string) => {
      state.days = days.value.calender
      state.selected = days.value.selected

      // カレンダー内で当月以外に属する日付を選択した場合に選択済み月表示も変更
      if (type === 'day' && picked.value.getMonth() !== state.selectedValue.month) {
        state.selectedValue.month = picked.value.getMonth()
      }
    }

    const openCalender = () => {
      const center = props.center
      const current = moment(center)

      state.isOpen = !state.isOpen
      state.selectedValue = {
        date: current.date(),
        year: current.year(),
        month: current.month(),
      }
      setDays()
    }

    const selectDate = (index: number) => {
      if (!props.center) {
        return
      }

      const day = state.days[index]
      state.selected = index
      state.selectedValue.date = day.getDate()
      picked.value = day
      setDays('day')
    }

    const selectYear = (value: number) => {
      state.selectedValue.year = value
      const element = document.getElementById('year-toggle') as HTMLInputElement
      element.checked = false

      picked.value = moment(picked.value.getTime()).year(value).toDate()
      setDays()
    }

    const selectMonth = (value: number) => {
      state.selectedValue.month = value
      if (state.reportType === 'day' || state.reportType === 'hour') {
        const element = document.getElementById('month-toggle') as HTMLInputElement
        element.checked = false
      }

      picked.value = moment(picked.value.getTime()).month(value).toDate()
      setDays()
    }

    const isLastRow = (index: number) => {
      const length = state.days.length
      const div = length / 7

      const lastRowBefore = (div - 1) * 7
      return index >= lastRowBefore
    }

    const isSelectedDay = (day: Date) => {
      const target = moment(day.getTime())
      const selected = moment(picked.value.getTime())

      if (!state.selectedValue.month) {
        return false
      }

      return target.date() === selected.date() && target.month() === selected.month()
    }

    const getDay = (date: Date) => {
      return moment(date.getTime()).date()
    }

    const pickDate = () => {
      const MonthElement = document.getElementById('month-toggle') as HTMLInputElement
      if (MonthElement) MonthElement.checked = false
      const YearElement = document.getElementById('month-toggle') as HTMLInputElement
      if (YearElement) YearElement.checked = false
      state.isOpen = false
      const date = state.days[state.selected] as Date
      let center = moment(date)
      center.set('year', state.selectedValue.year)
      center.set('month', state.selectedValue.month)
      const hour = center.hour()
      switch (state.reportType) {
        case XRange.HOUR:
          center = center.set('hour', Math.floor(hour / 8) * 8 + 4).startOf('hour')
          break
        case XRange.DAY:
          center = center.set('hour', 12)
          break
        case XRange.MONTH:
          center = center.set('date', 16)
          break
        case XRange.YEAR:
          center = center.set('month', 6).endOf('month')
          break
      }

      emit('update:center', center.set('hour', 12).valueOf())
    }

    watch(
      () => props.reportType,
      () => (state.reportType = props.reportType)
    )

    return {
      ...toRefs(state),
      openCalender,
      selectDate,
      isLastRow,
      isSelectedDay,
      selectYear,
      selectMonth,
      getDay,
      pickDate,
      picked,
      currentDateStr,
    }
  },
})
